import axios from "axios";
import { isOfflineMode } from "../utils/networkUtils";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  // baseURL: "http://localhost:3004",
  timeout: 60000, // 60 second timeout
});

// Add a request interceptor
api.interceptors.request.use(
  async (config) => {
    // Check if we're offline before making the request - force check to ensure it's current
    const isCurrentlyOffline = isOfflineMode();
   
    if (isCurrentlyOffline) {
      console.warn(
        `[API] Blocked network request while offline: ${
          config.method?.toUpperCase() || "unknown"
        } ${config.url || ""}`
      );
      return Promise.reject(
        new Error("Network request attempted while offline")
      );
    }

    

    const userDataFromLocalStorage = localStorage.getItem("user");
    const parsedUserData = userDataFromLocalStorage
      ? JSON.parse(userDataFromLocalStorage)
      : {};
    // Get the token from localStorage
    if (parsedUserData?.token) {
      config.headers.Authorization = `Bearer ${parsedUserData.token}`;
    }

    return config;
  },
  (error) => {
    console.error(`[API] Request error: ${error.message}`);
    return Promise.reject(error);
  }
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => {
   
    return response.data;
  },
  (error) => {
    // Handle network errors
    if (error.code === "ERR_NETWORK" || !error.response) {
      console.error(`[API] Network error: ${error.message}`);
      // Mark as offline if we detect network issues
      return Promise.reject(error);
    }

    // Handle authentication errors
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      console.error(
        `[API] Auth error (${error.response.status}): ${error.response.config.url}`
      );
      localStorage.removeItem("user");
      window.location.href = "/auth/signin"; // Redirect to the authentication page
    } else if (error.response) {
      console.error(
        `[API] Error ${
          error.response.status
        }: ${error.response.config.method.toUpperCase()} ${
          error.response.config.url
        }`
      );
    }

    // Otherwise, throw the original error
    throw error.response?.data || error;
  }
);

/**
 * Checks the health of the API and network connectivity
 * @returns {Promise<boolean>} Returns true if the API is healthy and accessible
 */
export const checkApiHealth = async () => {
  try {
    const testEndpoint = process.env.REACT_APP_BACKEND_URL + "/health";

    const testRequest = await fetch(testEndpoint, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-cache",
      timeout: 5000,
    }).catch((error) => {
      console.error(
        `[API] Health check failed: ${error.message || "Connection error"}`
      );
      return null;
    });

    if (!testRequest) {
      return false;
    }

    return true;
  } catch (error) {
    console.error("[API] Health check error:", error);
    return false;
  }
};

export default api;
